import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

const BUS_IMAGES = [
  {
    exteriorImage: 'https://storage.readdy-site.link/project_files/543a30f0-c088-40ca-b55f-d366a28be6b3/6be2f467-2bbc-4c8c-87ce-63ba4622b009_1.png?v=5bf4d584fc58938f3b7dddf94399505c',
    interiorImage: 'https://public.readdy.ai/ai/img_res/5d410211fdead6b872fa49bff39fd1d4.jpg',
    seatImage: 'https://storage.readdy-site.link/project_files/543a30f0-c088-40ca-b55f-d366a28be6b3/fc680a3c-0d78-4629-aa68-c69ab2c22107_Screenshot-2026-03-27-202940.png?v=8e4b6ba0dd68693a811992aecab9cc8b',
    badgeColorClass: 'bg-[#C9A84C]',
    year: '2022',
    length: '13.5 m',
  },
  {
    exteriorImage: 'https://static.readdy.ai/image/0de80e9fd237f0b8df79ee5619c3a653/e8be45e747f87393658268218ee8f961.png',
    interiorImage: 'https://readdy.ai/api/search-image?query=spacious%20luxury%20coach%20bus%20interior%20aisle%20view%20showing%20rows%20of%20premium%20leather%20reclining%20seats%20in%20dark%20charcoal%20and%20cream%2C%20modern%20overhead%20bins%2C%20reading%20lights%2C%20clean%20professional%20photography%2C%20high-end%20intercity%20bus%20interior&width=900&height=600&seq=fleet_int_2&orientation=landscape',
    seatImage: 'https://readdy.ai/api/search-image?query=close%20up%20of%20a%20single%20premium%20leather%20bus%20seat%20with%20armrest%2C%20USB%20charging%20port%2C%20recline%20button%2C%20soft%20warm%20ambient%20lighting%2C%20luxury%20coach%20interior%20detail%2C%20high%20quality%20travel%20seat%2C%20minimalist%20modern%20design&width=900&height=600&seq=fleet_seat_2&orientation=landscape',
    badgeColorClass: 'bg-emerald-600',
    year: '2023',
    length: '13.5 m',
  },
];

const SPEC_ICONS = [
  { icon: 'ri-group-line', key: 'fleet_page_spec_capacity', valKey: 'fleet_page_spec_val_capacity' },
  { icon: 'ri-calendar-line', key: 'fleet_page_spec_year' },
  { icon: 'ri-ruler-line', key: 'fleet_page_spec_length' },
  { icon: 'ri-speed-up-line', key: 'fleet_page_spec_engine', valKey: 'fleet_page_spec_val_engine' },
  { icon: 'ri-temp-cold-line', key: 'fleet_page_spec_climate', valKey: 'fleet_page_spec_val_climate' },
  { icon: 'ri-wifi-line', key: 'fleet_page_spec_wifi', valKey: 'fleet_page_spec_val_wifi' },
  { icon: 'ri-tv-2-line', key: 'fleet_page_spec_entertainment', valKey: 'fleet_page_spec_val_entertainment' },
  { icon: 'ri-battery-charge-line', key: 'fleet_page_spec_charging', valKey: 'fleet_page_spec_val_charging' },
];

const SAFETY_ICON_KEYS = [
  { icon: 'ri-shield-check-line', titleKey: 'fleet_page_safety_0_title', descKey: 'fleet_page_safety_0_desc' },
  { icon: 'ri-user-star-line', titleKey: 'fleet_page_safety_1_title', descKey: 'fleet_page_safety_1_desc' },
  { icon: 'ri-camera-line', titleKey: 'fleet_page_safety_2_title', descKey: 'fleet_page_safety_2_desc' },
  { icon: 'ri-first-aid-kit-line', titleKey: 'fleet_page_safety_3_title', descKey: 'fleet_page_safety_3_desc' },
  { icon: 'ri-map-2-line', titleKey: 'fleet_page_safety_4_title', descKey: 'fleet_page_safety_4_desc' },
  { icon: 'ri-fire-line', titleKey: 'fleet_page_safety_5_title', descKey: 'fleet_page_safety_5_desc' },
];

const HIGHLIGHT_KEYS = [
  'fleet_page_highlight_0', 'fleet_page_highlight_1', 'fleet_page_highlight_2', 'fleet_page_highlight_3',
  'fleet_page_highlight_4', 'fleet_page_highlight_5', 'fleet_page_highlight_6', 'fleet_page_highlight_7',
];

function SeatMap({ rows, seatsPerRow, extraSeat, total }: { rows: number; seatsPerRow: number; extraSeat: number; total: number }) {
  const { t } = useTranslation();
  const [hoveredSeat, setHoveredSeat] = useState<number | null>(null);
  let seatNumber = 0;

  return (
    <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
      <h4 className="text-white/60 text-xs font-semibold tracking-[0.25em] uppercase mb-4 text-center">
        {t('fleet_page_seatmap_title')} — {total} {t('fleet_page_seats_label')}
      </h4>
      <div className="relative bg-white/5 rounded-xl border border-white/10 p-4 overflow-x-auto">
        <div className="flex items-center justify-between mb-4">
          <span className="text-white/30 text-xs uppercase tracking-wider">{t('fleet_page_seatmap_front')}</span>
          <div className="w-8 h-8 flex items-center justify-center bg-[#C9A84C]/20 rounded-full">
            <i className="ri-steering-2-line text-[#C9A84C] text-base" />
          </div>
          <span className="text-white/30 text-xs uppercase tracking-wider">{t('fleet_page_seatmap_door')}</span>
        </div>
        <div className="flex flex-col gap-1.5 min-w-[200px]">
          {Array.from({ length: rows }).map((_, rowIdx) => {
            const rowSeats: number[] = [];
            for (let s = 0; s < seatsPerRow; s++) {
              seatNumber++;
              rowSeats.push(seatNumber);
            }
            return (
              <div key={rowIdx} className="flex items-center gap-1 justify-center">
                {rowSeats.map((num, sIdx) => (
                  <>
                    <button
                      key={num}
                      onMouseEnter={() => setHoveredSeat(num)}
                      onMouseLeave={() => setHoveredSeat(null)}
                      className={`w-7 h-6 rounded-sm text-[9px] font-bold transition-all duration-150 cursor-default whitespace-nowrap ${
                        hoveredSeat === num ? 'bg-[#C9A84C] text-white' : 'bg-white/10 text-white/40 hover:bg-[#C9A84C]/30'
                      }`}
                    >
                      {num}
                    </button>
                    {sIdx === 1 && <div key={`aisle-${rowIdx}`} className="w-3" />}
                  </>
                ))}
              </div>
            );
          })}
          {extraSeat > 0 && (
            <div className="flex items-center gap-1 justify-center mt-1">
              {Array.from({ length: extraSeat }).map(() => {
                seatNumber++;
                const num = seatNumber;
                return (
                  <button
                    key={num}
                    onMouseEnter={() => setHoveredSeat(num)}
                    onMouseLeave={() => setHoveredSeat(null)}
                    className={`w-7 h-6 rounded-sm text-[9px] font-bold transition-all duration-150 cursor-default whitespace-nowrap ${
                      hoveredSeat === num ? 'bg-[#C9A84C] text-white' : 'bg-white/10 text-white/40 hover:bg-[#C9A84C]/30'
                    }`}
                  >
                    {num}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex justify-between mt-4">
          <span className="text-white/30 text-xs uppercase tracking-wider">{t('fleet_page_seatmap_rear')}</span>
        </div>
      </div>
      <p className="text-white/30 text-[11px] text-center mt-3">{t('fleet_page_seatmap_note')}</p>
    </div>
  );
}

function BusCard({ busIndex, index }: { busIndex: number; index: number }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'exterior' | 'interior' | 'seat'>('exterior');
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.08 });
  const bus = BUS_IMAGES[busIndex];

  const nameKey = busIndex === 0 ? 'fleet_page_bus1_name' : 'fleet_page_bus2_name';
  const taglineKey = busIndex === 0 ? 'fleet_page_bus1_tagline' : 'fleet_page_bus2_tagline';
  const badgeKey = busIndex === 0 ? 'fleet_page_badge_flagship' : 'fleet_page_badge_newest';
  const imageMap = { exterior: bus.exteriorImage, interior: bus.interiorImage, seat: bus.seatImage };

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
      style={{ transitionDelay: `${index * 150}ms` }}
    >
      <div className="bg-white/5 border border-white/10 rounded-3xl overflow-hidden hover:border-[#C9A84C]/30 transition-colors duration-300">
        <div className="px-8 pt-8 pb-0 flex items-start justify-between">
          <div>
            <span className={`inline-block text-white text-[10px] font-bold tracking-[0.2em] uppercase px-3 py-1 rounded-full mb-3 ${bus.badgeColorClass}`}>
              {t(badgeKey)}
            </span>
            <h3 className="font-playfair text-3xl font-bold text-white">{t(nameKey)}</h3>
            <p className="text-white/40 text-sm mt-1">{t(taglineKey)}</p>
          </div>
          <div className="text-right">
            <p className="text-[#C9A84C] text-3xl font-bold font-playfair">57</p>
            <p className="text-white/40 text-xs uppercase tracking-wider">{t('fleet_page_seats_label')}</p>
          </div>
        </div>

        <div className="px-8 pt-6">
          <div className="flex gap-1 bg-white/5 rounded-full p-1 w-fit mb-4">
            {(['exterior', 'interior', 'seat'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeTab === tab ? 'bg-[#C9A84C] text-white' : 'text-white/40 hover:text-white/70'
                }`}
              >
                {tab === 'seat' ? t('fleet_page_tab_seat') : t(`fleet_page_tab_${tab}`)}
              </button>
            ))}
          </div>
          <div className="rounded-2xl overflow-hidden h-64 md:h-80">
            <img
              src={imageMap[activeTab]}
              alt={`${t(nameKey)} ${activeTab}`}
              className="w-full h-full object-cover object-center transition-all duration-500"
            />
          </div>
        </div>

        <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div>
            <h4 className="text-[#C9A84C] text-xs font-bold tracking-[0.25em] uppercase mb-5">{t('fleet_page_specs_title')}</h4>
            <div className="grid grid-cols-1 gap-3">
              {SPEC_ICONS.map((spec) => {
                let value = spec.valKey ? t(spec.valKey) : '';
                if (spec.key === 'fleet_page_spec_year') value = bus.year;
                if (spec.key === 'fleet_page_spec_length') value = bus.length;
                return (
                  <div key={spec.key} className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-[#C9A84C]/10 rounded-lg shrink-0">
                      <i className={`${spec.icon} text-[#C9A84C] text-sm`} />
                    </div>
                    <div className="flex items-center justify-between flex-1">
                      <span className="text-white/40 text-xs uppercase tracking-wider">{t(spec.key)}</span>
                      <span className="text-white text-sm font-medium">{value}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <SeatMap rows={14} seatsPerRow={4} extraSeat={1} total={57} />
        </div>

        <div className="px-8 pb-8">
          <h4 className="text-[#C9A84C] text-xs font-bold tracking-[0.25em] uppercase mb-4">{t('fleet_page_features_title')}</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {HIGHLIGHT_KEYS.map((key) => (
              <div key={key} className="flex items-center gap-2 text-white/60 text-sm">
                <i className="ri-check-line text-[#C9A84C] text-base shrink-0" />
                {t(key)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function HeroSection() {
  const { t } = useTranslation();
  return (
    <section className="relative pt-32 pb-20 bg-[#1a1a1a] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=luxury%20premium%20intercity%20coach%20bus%20driving%20on%20a%20scenic%20highway%20in%20Panama%20at%20sunrise%2C%20golden%20hour%20light%2C%20dramatic%20sky%2C%20professional%20automotive%20photography%2C%20side%20profile%20shot%2C%20cinematic%20wide%20angle%2C%20empty%20open%20road%2C%20lush%20green%20landscape&width=1600&height=700&seq=fleet_hero_01&orientation=landscape"
          alt="Phoenix Fleet"
          className="w-full h-full object-cover object-center opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a] via-[#1a1a1a]/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] to-transparent" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <button
          onClick={() => window.REACT_APP_NAVIGATE?.('/')}
          className="inline-flex items-center gap-2 text-white/60 hover:text-[#C9A84C] text-sm font-medium tracking-wider uppercase transition-colors duration-300 cursor-pointer mb-10 group"
        >
          <span className="w-7 h-7 flex items-center justify-center rounded-full border border-white/20 group-hover:border-[#C9A84C] transition-colors duration-300">
            <i className="ri-arrow-left-line text-sm" />
          </span>
          {t('common_backHome')}
        </button>
        <p className="text-[#C9A84C] text-xs font-semibold tracking-[0.35em] uppercase mb-5">{t('fleet_page_eyebrow')}</p>
        <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
          {t('fleet_page_h1_line1')} <br className="hidden md:block" />
          <span className="text-[#C9A84C]">{t('fleet_page_h1_line2')}</span>
        </h1>
        <p className="text-white/60 text-lg max-w-xl leading-relaxed mb-12">{t('fleet_page_subtitle')}</p>

        <div className="flex flex-wrap gap-8 md:gap-14">
          {[
            { icon: 'ri-bus-2-line', value: '2', labelKey: 'fleet_page_stat1_label' },
            { icon: 'ri-group-line', value: '114', labelKey: 'fleet_page_stat2_label' },
            { icon: 'ri-calendar-check-line', value: '365', labelKey: 'fleet_page_stat3_label' },
            { icon: 'ri-map-pin-line', value: '248 km', labelKey: 'fleet_page_stat4_label' },
          ].map((stat) => (
            <div key={stat.labelKey} className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-[#C9A84C]/15 rounded-full">
                <i className={`${stat.icon} text-[#C9A84C] text-lg`} />
              </div>
              <div>
                <div className="text-white font-bold text-xl font-playfair">{stat.value}</div>
                <div className="text-white/40 text-xs uppercase tracking-wider">{t(stat.labelKey)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SafetySection() {
  const { t } = useTranslation();
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={`w-full py-20 bg-[#111] transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <p className="text-[#C9A84C] text-xs font-semibold tracking-[0.35em] uppercase mb-4">{t('fleet_page_safety_eyebrow')}</p>
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-white">
            {t('fleet_page_safety_title1')} <span className="text-[#C9A84C]">{t('fleet_page_safety_title2')}</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SAFETY_ICON_KEYS.map((f) => (
            <div key={f.titleKey} className="bg-white/5 border border-white/8 rounded-2xl p-6 hover:border-[#C9A84C]/30 transition-colors duration-300">
              <div className="w-11 h-11 flex items-center justify-center bg-[#C9A84C]/10 rounded-xl mb-4">
                <i className={`${f.icon} text-[#C9A84C] text-xl`} />
              </div>
              <h3 className="text-white font-semibold text-base mb-2">{t(f.titleKey)}</h3>
              <p className="text-white/40 text-sm leading-relaxed">{t(f.descKey)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaBanner() {
  const { t } = useTranslation();
  return (
    <section className="py-20 bg-[#1a1a1a] relative overflow-hidden">
      <div className="absolute inset-0 opacity-10"
        style={{ backgroundImage: 'radial-gradient(ellipse at center, #C9A84C 0%, transparent 70%)' }}
      />
      <div className="relative max-w-3xl mx-auto px-6 lg:px-10 text-center">
        <p className="text-[#C9A84C] text-xs font-semibold tracking-[0.35em] uppercase mb-4">{t('fleet_page_cta_eyebrow')}</p>
        <h2 className="font-playfair text-3xl md:text-4xl font-bold text-white mb-5">{t('fleet_page_cta_title')}</h2>
        <p className="text-white/55 text-base mb-9 leading-relaxed">{t('fleet_page_cta_subtitle')}</p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={() => window.REACT_APP_NAVIGATE?.('/schedules')}
            className="px-8 py-3.5 bg-[#C9A84C] hover:bg-[#b8953e] text-white text-sm font-semibold tracking-wider uppercase rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            {t('fleet_page_cta_btn1')}
          </button>
          <button
            onClick={() => window.REACT_APP_NAVIGATE?.('/amenities')}
            className="px-8 py-3.5 border-2 border-white/20 hover:border-[#C9A84C] text-white/70 hover:text-[#C9A84C] text-sm font-semibold tracking-wider uppercase rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            {t('fleet_page_cta_btn2')}
          </button>
        </div>
      </div>
    </section>
  );
}

export default function FleetPage() {
  return (
    <div className="min-h-screen bg-[#1a1a1a] font-lato">
      <Navbar />
      <HeroSection />
      <section className="w-full bg-[#1a1a1a] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col gap-10">
            <BusCard busIndex={0} index={0} />
            <BusCard busIndex={1} index={1} />
          </div>
        </div>
      </section>
      <SafetySection />
      <CtaBanner />
      <Footer />
    </div>
  );
}
